import type { ChatPlayerTimedWord } from "./chat-player-types";

import { defaultLocale } from "@mw/shared/i18n";

type TextSegment = {
  text: string;
  isWordLike: boolean;
};

type IntlSegmenterConstructor = new (
  locale: string,
  options: { granularity: "word" },
) => {
  segment(input: string): Iterable<{ segment: string; isWordLike?: boolean }>;
};

export function clampMs(value: number, min = 0, max = Number.POSITIVE_INFINITY) {
  if (!Number.isFinite(value)) {
    return min;
  }

  return Math.min(max, Math.max(min, Math.round(value)));
}

export function secondsToMs(value: number | undefined) {
  if (typeof value !== "number") {
    return undefined;
  }

  return clampMs(value * 1000);
}

export function makeStableId(parts: Array<string | number | undefined>) {
  return parts
    .filter(part => part !== undefined && part !== "")
    .map(part =>
      String(part)
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-|-$/g, ""),
    )
    .filter(Boolean)
    .join("-");
}

export function segmentText(text: string, locale = defaultLocale) {
  const IntlWithSegmenter = Intl as typeof Intl & {
    Segmenter?: IntlSegmenterConstructor;
  };

  if (IntlWithSegmenter.Segmenter) {
    const segmenter = new IntlWithSegmenter.Segmenter(locale, {
      granularity: "word",
    });

    return Array.from(segmenter.segment(text), segment => ({
      text: segment.segment,
      isWordLike: Boolean(segment.isWordLike),
    })).filter(segment => segment.text.length > 0);
  }

  return (text.match(/\s+|\S+/g) ?? []).map(segment => ({
    text: segment,
    isWordLike: /\S/.test(segment),
  })) satisfies TextSegment[];
}

export function estimateSpeechDurationMs(text: string) {
  const segments = segmentText(text);

  return segments.reduce((duration, segment) => {
    if (!segment.isWordLike) {
      return duration + Math.min(segment.text.length * 8, 80);
    }

    return duration + clampMs(120 + segment.text.length * 28, 150, 520);
  }, 0);
}

export function synthesizeTimedWords({
  text,
  idPrefix,
  startMs,
  endMs,
  immediate = false,
}: {
  text: string;
  idPrefix: string;
  startMs: number;
  endMs?: number;
  immediate?: boolean;
}) {
  const segments = segmentText(text);
  const normalizedStart = clampMs(startMs);

  if (segments.length === 0) {
    return [];
  }

  const normalizedEnd =
    typeof endMs === "number"
      ? Math.max(normalizedStart, clampMs(endMs))
      : normalizedStart + estimateSpeechDurationMs(text);
  const durationMs = normalizedEnd - normalizedStart;

  if (immediate || durationMs === 0) {
    return segments.map((segment, index) => ({
      id: makeStableId([idPrefix, "word", index]) || `${idPrefix}-word-${index}`,
      text: segment.text,
      startMs: normalizedStart,
      endMs: normalizedStart,
    })) satisfies ChatPlayerTimedWord[];
  }

  const weights = segments.map(segment => {
    if (!segment.isWordLike) {
      return Math.max(0.12, segment.text.length * 0.04);
    }

    return Math.max(1, segment.text.trim().length);
  });
  const totalWeight = weights.reduce((sum, weight) => sum + weight, 0);
  let cursor = normalizedStart;

  return segments.map((segment, index) => {
    const tokenDuration =
      index === segments.length - 1
        ? normalizedEnd - cursor
        : Math.max(0, Math.round((durationMs * weights[index]) / totalWeight));
    const tokenStart = cursor;
    const tokenEnd = index === segments.length - 1 ? normalizedEnd : cursor + tokenDuration;
    cursor = tokenEnd;

    return {
      id: makeStableId([idPrefix, "word", index]) || `${idPrefix}-word-${index}`,
      text: segment.text,
      startMs: tokenStart,
      endMs: tokenEnd,
    };
  }) satisfies ChatPlayerTimedWord[];
}

export function normalizeTimedWordSpacing(words: ChatPlayerTimedWord[]) {
  const normalizedWords: ChatPlayerTimedWord[] = [];
  let pendingWhitespace = "";

  for (const word of words) {
    if (word.text.trim().length === 0) {
      if (normalizedWords.length > 0) {
        pendingWhitespace += word.text;
      }

      continue;
    }

    normalizedWords.push({
      ...word,
      text: `${pendingWhitespace}${word.text}`,
    });
    pendingWhitespace = "";
  }

  return normalizedWords;
}
